/**
 * Window World Chat Widget
 * Standard chatbot widget for answering customer questions and collecting leads.
 *
 * Usage:
 * <script>
 *   window.WWChatConfig = {
 *     api: {
 *       proxyUrl: '/wp-json/breeze/v1/chat',
 *       franchiseId: 'jersey-coast'
 *     },
 *     branding: {
 *       logo: 'https://...',
 *       name: 'Window World of Jersey Coast',
 *       assistantName: 'Winslow',
 *       regularWelcomeMessage: 'How can I help you with windows, doors, siding, or roofing today?',
 *       responseTimeText: 'We typically respond right away'
 *     },
 *     style: {
 *       primaryColor: '#1875df',
 *       secondaryColor: '#0a2681',
 *       position: 'right'
 *     }
 *   };
 * </script>
 * <script src="ww-chat-widget.js"></script>
 */
(function() {
    'use strict';

    // Prevent multiple initializations
    if (window.WWChatWidgetInitialized) return;
    window.WWChatWidgetInitialized = true;

    // ============== SPARKLE ICON ==============
    // Placeholder 4-pointed star — replace the <path> with your actual star SVG path
    const SPARKLE_SVG = `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L12.8 10.2L21 12L12.8 13.8L12 22L11.2 13.8L3 12L11.2 10.2Z" fill="currentColor"/>
    </svg>`;
    const scriptSrc = (document.currentScript && document.currentScript.src) ? document.currentScript.src : window.location.href;
    const DEFAULT_LAUNCHER_IMAGE_SRC = (() => {
        try {
            return new URL('../images/chat-bot-launch.png', scriptSrc).href;
        } catch (e) {
            return '/wp-content/themes/breeze/dist/images/chat-bot-launch.png';
        }
    })();
    const DEFAULT_RESPONSE_ICON_SRC = (() => {
        try {
            return new URL('../images/response.png', scriptSrc).href;
        } catch (e) {
            return '/wp-content/themes/breeze/dist/images/response.png';
        }
    })();

    // ============== DEFAULT CONFIG ==============
    const defaultConfig = {
        api: { proxyUrl: '', franchiseId: '' },
        branding: {
            logo: '',
            launcher: '',
            name: 'Window World',
            assistantName: 'Winslow',
            regularWelcomeMessage: '',
            responseTimeText: 'We typically respond right away',
            poweredBy: { text: 'Powered by TriMark Digital', link: 'https://www.trimarkdigital.com/' }
        },
        style: {
            primaryColor: '#1875df',
            secondaryColor: '#0a2681',
            position: 'right',
            backgroundColor: '#ffffff',
            fontColor: '#333333'
        },
        behavior: {
            launcherPromptDelaySeconds: 0
        }
    };

    // Merge config
    const config = window.WWChatConfig ? {
        api: { ...defaultConfig.api, ...window.WWChatConfig.api },
        branding: { ...defaultConfig.branding, ...window.WWChatConfig.branding },
        style: { ...defaultConfig.style, ...window.WWChatConfig.style },
        behavior: { ...defaultConfig.behavior, ...window.WWChatConfig.behavior }
    } : defaultConfig;
    const launcherImageSrc = (config.branding.launcher || '').trim() || DEFAULT_LAUNCHER_IMAGE_SRC;
    const responseIconSrc = (config.branding.launcher || '').trim() || DEFAULT_RESPONSE_ICON_SRC;

    const assistantName = config.branding.assistantName || 'Winslow';
    const regularWelcomeMessage = (config.branding.regularWelcomeMessage || config.branding.welcomeText || '').trim();
    const launcherPromptDelaySeconds = Number(config.behavior.launcherPromptDelaySeconds || 0);

    // ============== SESSION MANAGEMENT ==============
    const STORAGE_KEY = 'ww_chat_' + config.api.franchiseId;
    let savedSession = null;
    try { savedSession = JSON.parse(localStorage.getItem(STORAGE_KEY)); } catch(e) {}

    // Clear old sessions (24h)
    if (savedSession?.timestamp && (Date.now() - savedSession.timestamp) > 24 * 60 * 60 * 1000) {
        localStorage.removeItem(STORAGE_KEY);
        savedSession = null;
    }

    let sessionId = savedSession?.sessionId || ('chat-' + crypto.randomUUID());
    let leadComplete = savedSession?.leadComplete || false;
    let chatHistory = savedSession?.messages || [];

    function saveSession() {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
                sessionId, leadComplete, messages: chatHistory, timestamp: Date.now()
            }));
        } catch(e) {}
    }

    // ============== CREATE WIDGET ==============
    const widget = document.createElement('div');
    widget.className = 'ww-chat-widget';
    widget.style.setProperty('--ww-chat-primary-color', config.style.primaryColor);
    widget.style.setProperty('--ww-chat-secondary-color', config.style.secondaryColor);
    widget.style.setProperty('--ww-chat-background-color', config.style.backgroundColor);
    widget.style.setProperty('--ww-chat-font-color', config.style.fontColor);

    const positionClass = config.style.position === 'left' ? ' position-left' : '';

    widget.innerHTML = `
        <div class="chat-container${positionClass}">
            <div class="brand-header">
                <div class="brand-title-row">
                    <div class="brand-title">
                        <div class="brand-icon">${SPARKLE_SVG}</div>
                        <span class="brand-name">${assistantName}</span>
                    </div>
                    <button class="close-button">×</button>
                </div>
                <div class="brand-subtitle">${assistantName} is an AI virtual assistant, <a href="${config.branding.poweredBy?.link}" target="_blank">${config.branding.poweredBy?.text}</a></div>
            </div>
            <div class="lead-complete-banner" style="display: none;">
                ✓ A specialist will contact you soon!
            </div>
            <div class="chat-messages"></div>
            <div class="chat-input">
                <textarea placeholder="Ask me anything" rows="1"></textarea>
                <button type="submit">Send</button>
            </div>
            <div class="chat-footer"></div>
        </div>
        <div class="chat-launcher-prompt${positionClass}" hidden>
            <button type="button" class="chat-launcher-prompt__button" aria-label="Open chat">
                <span class="chat-launcher-prompt__avatar">
                    <img src="${responseIconSrc}" alt="" aria-hidden="true" loading="lazy">
                </span>
                <span class="chat-launcher-prompt__bubble">How can I help?</span>
            </button>
        </div>
        <button class="chat-toggle${positionClass}">
            <img src="${launcherImageSrc}" alt="Open chat" loading="lazy">
        </button>
    `;

    document.body.appendChild(widget);
    const brandIcon = widget.querySelector('.brand-icon');
    const brandingLogo = (config.branding.logo || '').trim();
    if (brandIcon) {
        if (brandingLogo) {
            brandIcon.innerHTML = `<img src="${brandingLogo}" alt="${config.branding.name || 'Window World'} logo" loading="lazy">`;
        } else {
            brandIcon.innerHTML = SPARKLE_SVG;
        }
    }

    // ============== ELEMENTS ==============
    const chatContainer = widget.querySelector('.chat-container');
    const messagesContainer = widget.querySelector('.chat-messages');
    const textarea = widget.querySelector('textarea');
    const sendButton = widget.querySelector('button[type="submit"]');
    const toggleButton = widget.querySelector('.chat-toggle');
    const launcherPrompt = widget.querySelector('.chat-launcher-prompt');
    const launcherPromptButton = widget.querySelector('.chat-launcher-prompt__button');
    const closeButton = widget.querySelector('.close-button');
    const leadBanner = widget.querySelector('.lead-complete-banner');
    let launcherPromptTimer = null;
    let launcherPromptDismissed = false;

    // ============== HELPERS ==============
    function parseMarkdown(text) {
        return text
            .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" loading="lazy">')
            .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
            .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>')
            .replace(/\n/g, '<br>');
    }

    // Wraps a content element in a bot-message-row with avatar
    function makeBotRow(contentEl) {
        const row = document.createElement('div');
        row.className = 'bot-message-row';
        const avatarDiv = document.createElement('div');
        avatarDiv.className = 'bot-avatar';
        avatarDiv.innerHTML = `<img src="${responseIconSrc}" alt="" aria-hidden="true" loading="lazy">`;
        row.appendChild(avatarDiv);
        row.appendChild(contentEl);
        return row;
    }

    function addMessage(text, isUser, isTyping = false, skipSave = false) {
        let element;
        if (isUser) {
            element = document.createElement('div');
            element.className = `chat-message user${isTyping ? ' typing' : ''}`;
            element.innerHTML = text;
            messagesContainer.appendChild(element);
        } else {
            const msgDiv = document.createElement('div');
            msgDiv.className = `chat-message bot${isTyping ? ' typing' : ''}`;
            msgDiv.innerHTML = parseMarkdown(text);
            element = makeBotRow(msgDiv);
            messagesContainer.appendChild(element);
        }
        messagesContainer.scrollTop = messagesContainer.scrollHeight;

        if (!isTyping && !skipSave) {
            chatHistory.push({ text, isUser });
            saveSession();
        }
        return element;
    }

    function showTyping() {
        const dotsDiv = document.createElement('div');
        dotsDiv.className = 'typing-dots';
        dotsDiv.innerHTML = '<span></span><span></span><span></span>';
        const row = makeBotRow(dotsDiv);
        messagesContainer.appendChild(row);
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
        return row;
    }

    function clearLauncherPromptTimer() {
        if (!launcherPromptTimer) return;
        clearTimeout(launcherPromptTimer);
        launcherPromptTimer = null;
    }

    function hideLauncherPrompt(markDismissed = false) {
        if (!launcherPrompt) return;
        if (markDismissed) {
            launcherPromptDismissed = true;
            clearLauncherPromptTimer();
        }
        launcherPrompt.classList.remove('is-visible');
        launcherPrompt.hidden = true;
    }

    function showLauncherPrompt() {
        if (!launcherPrompt || launcherPromptDismissed || chatContainer.classList.contains('open')) {
            return;
        }
        launcherPrompt.hidden = false;
        requestAnimationFrame(() => {
            launcherPrompt.classList.add('is-visible');
        });
    }

    // ============== API CALL ==============
    async function sendMessage(message) {
        addMessage(message, true);
        textarea.disabled = true;
        sendButton.disabled = true;

        const typingDiv = showTyping();

        try {
            const response = await fetch(config.api.proxyUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    message: message,
                    session_id: sessionId,
                    mode: 'default'
                })
            });

            const data = await response.json();
            typingDiv.remove();
            addMessage(data.response, false);

            if (data.lead_complete && !leadComplete) {
                leadComplete = true;
                leadBanner.style.display = 'block';
                saveSession();
            }

        } catch (error) {
            console.error('Chat error:', error);
            typingDiv.remove();
            addMessage('Sorry, there was an error. Please try again.', false);
        }

        textarea.disabled = false;
        sendButton.disabled = false;
        textarea.focus();
    }

    // ============== RESTORE SESSION / WELCOME ==============
    if (chatHistory.length > 0) {
        chatHistory.forEach(msg => addMessage(msg.text, msg.isUser, false, true));
        if (leadComplete) leadBanner.style.display = 'block';
    } else {
        const defaultWelcomeMessage = `Welcome to ${config.branding.name}. How can I help you with windows, doors, siding, or roofing today?`;
        const welcomeMessage = regularWelcomeMessage || defaultWelcomeMessage;
        // Welcome message renders without star avatar — subsequent bot messages get the avatar
        const msgDiv = document.createElement('div');
        msgDiv.className = 'chat-message bot welcome-message';
        msgDiv.innerHTML = `<span class="welcome-hello">Hello!</span> <span class="welcome-name">I'm ${assistantName}!</span><br>${parseMarkdown(welcomeMessage)}`;
        messagesContainer.appendChild(msgDiv);
    }

    // ============== EVENT LISTENERS ==============
    toggleButton.addEventListener('click', () => {
        chatContainer.classList.toggle('open');
        if (chatContainer.classList.contains('open')) {
            hideLauncherPrompt(true);
            setTimeout(() => messagesContainer.scrollTop = messagesContainer.scrollHeight, 50);
        }
    });

    closeButton.addEventListener('click', () => chatContainer.classList.remove('open'));

    document.addEventListener('click', (event) => {
        if (!chatContainer.classList.contains('open')) {
            return;
        }

        if (widget.contains(event.target)) {
            return;
        }

        chatContainer.classList.remove('open');
    });

    if (launcherPromptButton) {
        launcherPromptButton.addEventListener('click', (event) => {
            event.preventDefault();
            hideLauncherPrompt(true);
            if (!chatContainer.classList.contains('open')) {
                chatContainer.classList.add('open');
                setTimeout(() => {
                    messagesContainer.scrollTop = messagesContainer.scrollHeight;
                    textarea.focus();
                }, 50);
                return;
            }
            textarea.focus();
        });
    }

    sendButton.addEventListener('click', () => {
        const msg = textarea.value.trim();
        if (msg) { sendMessage(msg); textarea.value = ''; }
    });

    textarea.addEventListener('keypress', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            const msg = textarea.value.trim();
            if (msg) { sendMessage(msg); textarea.value = ''; }
        }
    });

    if (launcherPromptDelaySeconds > 0 && launcherPrompt) {
        launcherPromptTimer = setTimeout(() => {
            showLauncherPrompt();
        }, launcherPromptDelaySeconds * 1000);
    }

})();
